import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import Cookies from 'js-cookie'
import { COOKIE_NAME, setConsent } from '../../lib/consent'

// Dispatch on window to reopen the banner so the visitor can change the answer (footer "Cookie settings").
export const OPEN_SETTINGS_EVENT = 'martintamm:open-cookie-settings'

const CURRENT_CHOICE = {
  true: 'Your current choice: analytics cookies accepted.',
  false: 'Your current choice: analytics cookies declined.'
}

const buttonClass =
  'inline-flex min-h-11 items-center justify-center border-2 px-5 font-sans text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-link focus-visible:ring-offset-2'

// 'first': not answered yet, not closable. 'reopened': opened via OPEN_SETTINGS_EVENT, closable.
function initialMode() {
  return Cookies.get(COOKIE_NAME) === undefined ? 'first' : null
}

export default function ConsentBanner() {
  const [mode, setMode] = useState(initialMode)
  const [current, setCurrent] = useState(null)
  const regionRef = useRef(null)
  const currentRef = useRef(null)
  const returnFocusRef = useRef(null)

  // Layout effect: React runs passive effects some time after the footer is on screen (long after while
  // a lazy route loads on a busy main thread), and an early "Cookie settings" click found no listener.
  useLayoutEffect(() => {
    const open = () => {
      if (Cookies.get(COOKIE_NAME) === undefined) {
        // Not answered yet: the first-visit banner is already showing; just move focus to it.
        setMode('first')
        regionRef.current?.focus()
        return
      }
      returnFocusRef.current = document.activeElement
      setCurrent(Cookies.get(COOKIE_NAME))
      setMode('reopened')
    }
    window.addEventListener(OPEN_SETTINGS_EVENT, open)
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, open)
  }, [])

  const close = useCallback(() => {
    setMode(null)
    const target = returnFocusRef.current
    returnFocusRef.current = null
    if (target && target.isConnected) target.focus()
  }, [])

  useEffect(() => {
    if (mode !== 'reopened') return undefined
    ;(currentRef.current ?? regionRef.current)?.focus()
    const onKeyDown = (event) => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [mode, current, close])

  if (!mode) return null

  // Close first so focus returns before a withdraw reloads the page.
  const answer = (value) => {
    close()
    setConsent(value)
  }

  const reopened = mode === 'reopened'
  const currentText = reopened ? CURRENT_CHOICE[current] : undefined

  return (
    <section
      ref={regionRef}
      tabIndex={-1}
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-50 border-t-2 border-ink bg-paper focus:outline-none"
    >
      <div className="mx-auto flex max-w-[1180px] flex-col gap-4 px-4 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div className="text-sm text-ink">
          {currentText && (
            <p ref={currentRef} tabIndex={-1} className="mb-2 font-semibold focus:outline-none">
              {currentText}
            </p>
          )}
          <p>
            This website uses cookies to enhance your browsing experience and analyze site traffic. By clicking
            &quot;Accept All Cookies&quot;, you consent to the use of cookies as described in our{' '}
            <Link to="/privacy-policy" className="text-link underline underline-offset-4">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-3">
          <button type="button" onClick={() => answer('false')} className={`${buttonClass} border-ink bg-paper text-ink`}>
            Decline
          </button>
          <button type="button" onClick={() => answer('true')} className={`${buttonClass} border-ink bg-ink text-paper`}>
            Accept All Cookies
          </button>
          {reopened && (
            <button
              type="button"
              onClick={close}
              aria-label="Close cookie settings"
              className={`${buttonClass} min-w-11 border-ink bg-paper px-0 text-lg text-ink`}
            >
              ×
            </button>
          )}
        </div>
      </div>
    </section>
  )
}
